import {
  AudioPlayer,
  AudioPlayerOptions,
  AudioPlayerPlayOptions,
  AudioPlayerStatus
} from "../../models/audio";
import { getAudioContext } from "../microphone/input/web-audio-api";
import { MPEGDecoder } from "mpg123-decoder";

const max = (a: number, b: number) => (a > b ? a : b);
const min = (a: number, b: number) => (a < b ? a : b);
const floatToInt = (val: number) =>
  val > 0 ? min(val * 32767, 32767) : max(val * 32767, -32768);

const getInterleaved = (channelData: Float32Array[], samples: number) => {
  const interleaved = new Int16Array(samples * channelData.length);

  for (let offset = 0, interleavedOffset = 0; offset < samples; offset++) {
    interleaved[interleavedOffset++] = floatToInt(channelData[0][offset]);
    interleaved[interleavedOffset++] = floatToInt(channelData[1][offset]);
  }

  return new Uint8Array(interleaved.buffer);
};

const stringToUint8Array = (string: string) => {
  const buf = new Uint8Array(string.length);

  for (let i = 0; i < string.length; i++) buf[i] = string.charCodeAt(i);

  return buf;
};

const generateHeader = ({
  dataLength = 4294967295 - 100,
  channels = 2,
  sampleRate = 44100,
  bitDepth = 16
}) => {
  const RIFF = stringToUint8Array("RIFF");
  const WAVE = stringToUint8Array("WAVE");
  const fmt = stringToUint8Array("fmt ");
  const data = stringToUint8Array("data");

  const format = 1; // raw PCM

  const headerLength = 44;
  const fileSize = headerLength + dataLength;

  const header = new Uint8Array(headerLength);
  const headerView = new DataView(header.buffer);

  let offset = 0;

  header.set(RIFF, offset);
  offset += RIFF.length;

  headerView.setInt32(offset, fileSize - 8, true);
  offset += 4;

  header.set(WAVE, offset);
  offset += WAVE.length;

  header.set(fmt, offset);
  offset += fmt.length;

  // Write the size of the "fmt " chunk.
  // Value of 16 is hard-coded for raw PCM format. other formats have different size.
  headerView.setUint32(offset, 16, true);
  offset += 4;

  headerView.setUint16(offset, format, true);
  offset += 2;

  headerView.setUint16(offset, channels, true);
  offset += 2;

  headerView.setUint32(offset, sampleRate, true);
  offset += 4;

  const byteRate = (sampleRate * channels * bitDepth) / 8;
  headerView.setUint32(offset, byteRate, true);
  offset += 4;

  const blockAlign = (channels * bitDepth) / 8;
  headerView.setUint16(offset, blockAlign, true);
  offset += 2;

  headerView.setUint16(offset, bitDepth, true);
  offset += 2;

  header.set(data, offset);
  offset += data.length;

  headerView.setUint32(offset, dataLength, true);
  offset += 4;

  return header;
};

export abstract class AudioBasePlayer implements AudioPlayer {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  protected _audioContext: AudioContext;
  protected _decoder: MPEGDecoder;
  protected _source!: AudioBufferSourceNode;

  protected _status: AudioPlayerStatus = "ready";
  protected _currentlyPlayedIndex = -1;
  protected _audioUrls: string[] = [];

  protected _debug: boolean;
  protected _logs: any[] = [];

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: AudioPlayerOptions = {}) {
    this._audioContext = getAudioContext(options.audioContext);

    this._decoder = new MPEGDecoder();

    this._debug = options.debug || true;

    if (this._audioContext) {
      this._source = this._audioContext.createBufferSource();
      this.log(
        `AudioDecoderPlayer init: AudioContext ${this._audioContext.state}`
      );
    }
  }

  // --------------------------------------------------------------------------
  //    Getter
  // --------------------------------------------------------------------------
  get audioContext(): AudioContext {
    return this._audioContext;
  }

  get audioUrls(): string[] {
    return this._audioUrls;
  }

  get currentlyPlayedIndex(): number | undefined {
    return this._currentlyPlayedIndex >= 0
      ? this._currentlyPlayedIndex
      : undefined;
  }

  get logs(): any[] {
    return this._logs;
  }

  get status(): AudioPlayerStatus {
    return this._status;
  }

  // --------------------------------------------------------------------------
  //    Methods
  // --------------------------------------------------------------------------
  async destroy() {
    this.stop();
    await this._decoder?.free();
  }

  async onUserInteraction() {
    this._audioContext = getAudioContext(this._audioContext);

    await this._audioContext.resume();
  }

  // method used by audio-decoder package demo
  // https://github.com/eshaz/wasm-audio-decoders/blob/master/demo/index.html
  createArrayBuffer(
    channelData: Float32Array[],
    samplesDecoded: number,
    sampleRate: number
  ): ArrayBuffer {
    const interleaved = getInterleaved(channelData, samplesDecoded);
    const waveHeader = generateHeader({
      dataLength: interleaved.length * Int16Array.BYTES_PER_ELEMENT,
      channels: channelData.length,
      sampleRate,
      bitDepth: 16
    });

    const decoded = new Uint8Array(waveHeader.length + interleaved.length);
    decoded.set(waveHeader);
    decoded.set(interleaved, waveHeader.length);

    return decoded.buffer;
  }

  async decodeAudio(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
    await this._decoder.ready;

    const array = new Uint8Array(arrayBuffer);
    const { channelData, samplesDecoded, sampleRate } = this._decoder.decode(
      array
    );
    await this.resetDecoder();

    return this.createArrayBuffer(channelData, samplesDecoded, sampleRate);
  }

  decodeAudioData(audioBuffer: ArrayBuffer): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      this.audioContext?.decodeAudioData(
        audioBuffer,
        (decodedAudioData: AudioBuffer) => {
          resolve(decodedAudioData);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  decodeAudioSuccess(decodedData: AudioBuffer, onNext: () => void) {
    console.log("audio successfully decoded");

    this.log(
      `Audio#${this._currentlyPlayedIndex} decoding ${decodedData.length} bytes`
    );

    this._source = this._audioContext.createBufferSource();
    this._source.buffer = decodedData;

    this._source.onended = () => {
      onNext();
    };

    this._status = "playing";

    this._source.connect(this._audioContext.destination);

    // https://developer.mozilla.org/en-US/docs/Web/API/AudioBufferSourceNode/start
    // https://www.caniuse.com/#feat=mdn-api_audiobuffersourcenode_start
    this._source.start(0);
    this.log(
      `Audio#${this._currentlyPlayedIndex} state: ${this._source.context.state}`
    );
  }

  async fallbackDecode(
    arrayBuffer: ArrayBuffer,
    onNext: () => void,
    onError: (error: any) => void
  ) {
    console.log("attempting fallback decode");
    const decodedArrayBuffer = await this.decodeAudio(arrayBuffer);

    try {
      const decodedAudioData = await this.decodeAudioData(decodedArrayBuffer);

      await this.decodeAudioSuccess(decodedAudioData, onNext);
    } catch (error) {
      console.log("fallback decoding failed", error);
      onError(error);
    }
  }

  async play(
    urls: string | string[],
    options: AudioPlayerPlayOptions = {}
  ): Promise<void> {
    const audioUrls = urls instanceof Array ? urls : [urls];
    await this.playAudioIndex(audioUrls, 0, options);
  }

  protected async playAudioIndex(
    urls: string[],
    index: number,
    options: AudioPlayerPlayOptions
  ): Promise<void> {
    const playNextOrEnd = () => {
      this._status = "ready";
      if (this._currentlyPlayedIndex < urls.length - 1) {
        this.playAudioIndex(urls, index + 1, options);
      } else if (options.onEnded) {
        options.onEnded();
      }
    };

    const onError = (url: string) => (err: any) => {
      const error = {
        audioUrl: url,
        currentlyPlayedIndex: this._currentlyPlayedIndex,
        error: err,
        errorAsStr: JSON.stringify(err)
      };

      if (options.onError) {
        options.onError(error);
      }

      this.log(
        `Audio#${this._currentlyPlayedIndex} err: ${JSON.stringify(error)}`
      );
      playNextOrEnd();
    };

    const url = urls[index];
    this._currentlyPlayedIndex = index;

    this.log(`Audio#${this._currentlyPlayedIndex} start`);
    try {
      await this.playAudioUrl(url, playNextOrEnd, onError(url));
    } catch (err) {
      onError(url)(err);
    }
  }

  protected abstract playAudioUrl(
    url: string,
    onNext: () => void,
    onError: (err: any) => void
  ): Promise<void>;

  pause() {
    if (this._status === "playing") {
      this.doPause();
      this._status = "paused";
    }
  }

  resetDecoder = async () => {
    await this._decoder?.reset();
  };

  resume() {
    if (this._status === "paused") {
      this.doResume();
      this._status = "playing";
    }
  }

  stop() {
    if (this._status !== "ready") {
      this.doStop();
    }
    this._status = "ready";
  }

  abstract doPause(): void;
  abstract doResume(): void;
  abstract doStop(): void;

  // --------------------------------------------------------------------------
  //    Methods: logs
  // --------------------------------------------------------------------------
  protected log(input: any) {
    if (this._debug) {
      this._logs = [input, ...this._logs];
    }
  }
}
